<script setup lang="ts">
/**
 * /takeoff-applications —— 起飞申请列表与审核抽屉(S-53)。
 *
 * 飞手在起飞前提交一份申报(起点、终点、计划起飞时刻、机型、货物),这里看到全部申请,
 * 行尾「审核」打开抽屉:展示**全部申报内容** + 通过 / 驳回 + 意见输入。
 *
 * ═══ 这一页有全系统唯一一条监管端写调用 ═══
 *
 * 这个端在此之前是全局只读的。审核起飞申请是**唯一**一条写路由(Y-36),
 * 于是这里也是监管端前端里唯一一处 `api.` 的写方法调用(reviewTakeoffApplication)。
 * guards/check-ui-design-system.mjs 的写调用白名单里钉死的正是「这个方法 + 这个路径」——
 * 在页面里多调一个别的写方法、或在 client 里给它换一条路径,那条守卫会当场报红。
 * 所以这一页**不写任何别的写动作**:没有删除、没有改单、没有批量。
 *
 * ═══ 已审核的申请不再给审核入口 ═══
 *
 * 已通过 / 已驳回是**终态,没有改判**(Y-40):再审核一次服务端返回 409。
 * 所以非待审核的行,行尾按钮画的是「查看」而不是「审核」——
 * 别造一个点下去会报错的按钮(那是「界面上有个动作而它做不成」)。
 * 抽屉里也只对待审核的行画那两颗按钮。
 *
 * ═══ 这一页是一份不完整的台账(Y-41,选定的范围) ═══
 *
 * 审核只留痕不挡作业(Y-39),飞手不交也能飞,而「哪些单该交没交」这个视角
 * 本轮明确不做 —— 所以这里只看得到「收到的申请」,漏交的单不出现在任何界面上。
 * 这是选定的范围,不是漏做;口径写进页头的说明里,免得被当成缺一页。
 *
 * ═══ 零金额(L0-01) ═══
 *
 * 契约的 TakeoffApplication 里没有任何金额字段,这一页也就不显示任何金额。
 * guards/check-gov-readonly.mjs 与 check-ui-design-system.mjs 都会查:
 * 视图里出现 amount / fee / price 这类名字即报红。**不要**从别的接口借一个金额过来。
 *
 * 时间走 @sea-drone/shared 的 formatShanghaiMinute(Y-03):库里存 UTC、
 * 给人看的是东八区。手写 toLocaleString 会在容器 TZ=UTC 时显示早 8 小时的时刻,
 * 而页面看上去完全正常。
 */
import { computed, h, onMounted, ref } from 'vue'
import { NButton, NDrawer, NDrawerContent, NInput, NSelect, NSpace, useMessage } from 'naive-ui'
import {
  DataTable,
  ErrorBanner,
  FieldGrid,
  NavIcon,
  PageHeader,
  PanelCard,
  StatusTag,
  cargoLabel,
  takeoffApplicationStatusGroup,
  takeoffApplicationStatusLabel,
  type ScopeNote,
} from '@sea-drone/ui'
import {
  TAKEOFF_APPLICATION_STATUS,
  formatCm3AsM3,
  formatGramsAsKg,
  formatShanghaiMinute,
} from '@sea-drone/shared'
import { api, ApiError, type TakeoffApplication } from '../api/client'

const message = useMessage()

const PAGE_SIZE = 50
/** 审核意见上限(契约 ReviewTakeoffApplicationInput.note maxLength;驳回时下限 1) */
const REVIEW_NOTE_MAX = 200

const rows = ref<TakeoffApplication[]>([])
const total = ref(0)
const page = ref(1)
const loading = ref(false)
const lastError = ref<string | null>(null)

// ── 筛选:状态下拉(中文名走单点表)/ 机型(模糊)/ 订单号 ────────────
const filterStatus = ref<string | null>(null)
const filterAircraftModel = ref('')
const filterOrderNo = ref('')

/**
 * 状态选项。取值从 shared 的 TAKEOFF_APPLICATION_STATUS 来(单点定义),
 * 中文名从 packages/ui 的 takeoffApplicationStatusLabel 来 ——
 * 视图里手写「待审核」三个字反而是违例(enum-labels 守卫 ③ 会当场报红)。
 */
const statusOptions = TAKEOFF_APPLICATION_STATUS.map((v) => ({ label: takeoffApplicationStatusLabel(v), value: v }))

const PAGE_NOTES: readonly ScopeNote[] = [
  {
    title: '这一页只看「收到的申请」',
    body:
      '审核只留痕、不挡飞手作业,而「哪些单该交没交」这个视角本轮不做 —— 所以这里是一份' +
      '**不完整的台账**:没提交申请的单不出现在任何界面上。这是选定的范围,不是漏做。',
    refs: ['Y-39', 'Y-41'],
  },
  {
    title: '监管端在这里唯一能做的写动作是审核',
    body:
      '这个端其余地方全程只读;审核起飞申请是全系统唯一一条监管端写路由,而且判据钉死到' +
      '「这一个方法 + 这一个路径」。已通过 / 已驳回的申请没有改判,再审核会返回冲突,所以' +
      '非待审核的行不画审核入口。全程看不到任何金额字段。',
    refs: ['Y-36', 'Y-40', 'L0-01'],
  },
]

// ── 审核抽屉 ─────────────────────────────────────────────────
/**
 * 抽屉里正在审的那一条,**从列表里现取**(与运营端承运商审核抽屉同一种做法):
 * 审核之后 refresh() 会换掉列表,存一份快照的抽屉会一直显示旧状态,
 * 而表格里那一行已经是新的 —— 同一个页面上两个状态互相矛盾。
 */
const targetId = ref<string | null>(null)
const target = computed(() => rows.value.find((r) => r.id === targetId.value) ?? null)

/** 抽屉打开时拉一次详情(全量申报内容),失败要显式说出来,不许拿列表行冒充 */
const detail = ref<TakeoffApplication | null>(null)
const detailLoading = ref(false)
const detailError = ref<string | null>(null)

/** 显示用:优先详情,没拉到就退回列表那一行(两者的 schema 同形) */
const shown = computed(() => detail.value ?? target.value)

const note = ref('')
const submitting = ref(false)

async function loadDetail(id: string): Promise<void> {
  detailLoading.value = true
  detailError.value = null
  try {
    const d = await api.getTakeoffApplication(id)
    if (targetId.value === id) detail.value = d
  } catch (e) {
    if (targetId.value === id) {
      detail.value = null
      detailError.value = e instanceof ApiError ? `[${e.code}] ${e.message}` : String(e)
    }
  } finally {
    if (targetId.value === id) detailLoading.value = false
  }
}

function openReview(row: TakeoffApplication): void {
  targetId.value = row.id
  detail.value = null
  detailError.value = null
  note.value = ''
  void loadDetail(row.id)
}

const drawerOpen = computed({
  get: () => target.value !== null,
  set: (open: boolean) => {
    if (!open) {
      targetId.value = null
      detail.value = null
      detailError.value = null
      detailLoading.value = false
      note.value = ''
    }
  },
})

async function submitReview(approve: boolean): Promise<void> {
  const current = target.value
  if (!current || submitting.value) return
  const text = note.value.trim()
  // 驳回必须填意见(AS-100);通过选填。前端拦一道,后端也会拦 ——
  // 这一道是为了让人当场看到「为什么不行」,不是替代服务端校验。
  if (!approve && text.length === 0) {
    message.warning('驳回必须填审核意见(1~200 字)')
    return
  }
  if (text.length > REVIEW_NOTE_MAX) {
    message.warning(`审核意见最多 ${REVIEW_NOTE_MAX} 字`)
    return
  }
  submitting.value = true
  try {
    const result = await api.reviewTakeoffApplication(current.id, {
      approve,
      ...(text === '' ? {} : { note: text }),
    })
    message.success(`${result.orderNo} 已${approve ? '通过' : '驳回'}`)
    await refresh()
    // 抽屉还开着时它必须与库里一致(状态、审核意见、审核时间)
    if (targetId.value === current.id) void loadDetail(current.id)
  } catch (e) {
    show(e)
  } finally {
    submitting.value = false
  }
}

function show(e: unknown): void {
  const msg =
    e instanceof ApiError
      ? `[${e.code}] ${e.message}（requestId ${e.requestId}）`
      : e instanceof Error
        ? e.message
        : String(e)
  lastError.value = msg
  message.error(msg, { duration: 8000 })
}

async function refresh(): Promise<void> {
  loading.value = true
  try {
    const result = await api.listTakeoffApplications({
      page: page.value,
      pageSize: PAGE_SIZE,
      ...(filterStatus.value ? { status: filterStatus.value as TakeoffApplication['status'] } : {}),
      ...(filterAircraftModel.value.trim() ? { aircraftModel: filterAircraftModel.value.trim() } : {}),
      ...(filterOrderNo.value.trim() ? { orderNo: filterOrderNo.value.trim() } : {}),
    })
    rows.value = result.items
    total.value = result.total
    lastError.value = null
  } catch (e) {
    show(e)
  } finally {
    loading.value = false
  }
}

/** 改筛选条件就回到第一页再取 —— 留在第 3 页会看到「这一页没有」而以为筛没了 */
function applyFilters(): void {
  page.value = 1
  void refresh()
}

/** 清空筛选:三件一起清,否则「清空」只清了看得见的那一半 */
function clearFilters(): void {
  filterStatus.value = null
  filterAircraftModel.value = ''
  filterOrderNo.value = ''
  applyFilters()
}

function goPage(next: number): void {
  page.value = next
  void refresh()
}

/** 区域没标注时画「未标注」—— 空着读起来像「这里没有这一项」,它是「提交时没填」 */
function routeText(row: TakeoffApplication): string {
  return `${row.origin.area ?? '未标注'} → ${row.destination.area ?? '未标注'}`
}

const columns = [
  { title: '订单号', key: 'orderNo', width: 170, render: (row: TakeoffApplication) => row.orderNo },
  {
    title: '提交时间',
    key: 'submittedAt',
    width: 150,
    render: (row: TakeoffApplication) => formatShanghaiMinute(row.submittedAt),
  },
  {
    title: '计划起飞时间',
    key: 'plannedTakeoffAt',
    width: 150,
    render: (row: TakeoffApplication) => formatShanghaiMinute(row.plannedTakeoffAt),
  },
  { title: '机型', key: 'aircraftModel', width: 150, render: (row: TakeoffApplication) => row.aircraftModel },
  { title: '起点 → 终点', key: 'route', width: 200, render: (row: TakeoffApplication) => routeText(row) },
  {
    title: '状态',
    key: 'status',
    width: 100,
    render: (row: TakeoffApplication) =>
      h(StatusTag, {
        status: row.status,
        label: takeoffApplicationStatusLabel(row.status),
        group: takeoffApplicationStatusGroup(row.status),
        tone: 'dark',
        size: 'sm',
      }),
  },
  { title: '提交人', key: 'pilotName', width: 120, render: (row: TakeoffApplication) => row.pilotName ?? '—' },
  {
    title: '操作',
    key: 'action',
    width: 90,
    render: (row: TakeoffApplication) =>
      h(
        NButton,
        { size: 'tiny', onClick: () => openReview(row) },
        // 已审核的申请没有改判:行尾只给「查看」,不画一个点下去会 409 的「审核」
        { default: () => (row.status === 'pending' ? '审核' : '查看') },
      ),
  },
]

/** 抽屉里「申报内容」那一张卡的键值对 —— 全部来自申请行(提交时刻的快照,Y-37) */
const declaredFields = computed(() => {
  const d = shown.value
  if (!d) return []
  return [
    { label: '订单号', value: d.orderNo, mono: true },
    { label: '提交时间', value: formatShanghaiMinute(d.submittedAt) },
    { label: '计划起飞时间', value: formatShanghaiMinute(d.plannedTakeoffAt) },
    { label: '机型', value: d.aircraftModel },
    { label: '起点区域', value: d.origin.area },
    { label: '起点地址', value: d.origin.address },
    { label: '终点区域', value: d.destination.area },
    { label: '终点地址', value: d.destination.address },
    { label: '货物类型', value: cargoLabel(d.cargoCategory) },
    { label: '货物描述', value: d.cargoDescription, span: 2 as const },
    // Y-59:申报里存的是整数克 / 立方厘米,这一页给人看的是 kg / m³
    { label: '重量', value: `${formatGramsAsKg(d.weightGram)} kg` },
    { label: '体积', value: d.volumeCm3 === null ? null : `${formatCm3AsM3(d.volumeCm3)} m³` },
    { label: '是否超规格', value: d.oversized ? '是' : '否' },
    { label: '提交人', value: d.pilotName },
    { label: '承运商', value: d.carrierName },
  ]
})

/** 已审核的行:结论 / 意见 / 时间 / 人 */
const reviewFields = computed(() => {
  const d = shown.value
  if (!d || d.status === 'pending') return []
  return [
    { label: '审核结论', value: takeoffApplicationStatusLabel(d.status) },
    { label: '审核意见', value: d.reviewNote, span: 2 as const },
    { label: '审核时间', value: d.reviewedAt ? formatShanghaiMinute(d.reviewedAt) : null },
    { label: '审核人', value: d.reviewedBy },
  ]
})

const pageCount = computed(() => Math.max(1, Math.ceil(total.value / PAGE_SIZE)))

onMounted(() => {
  void refresh()
})
</script>

<template>
  <div class="page">
    <PageHeader tone="dark" title="起飞申请" :notes="PAGE_NOTES">
      <template #actions>
        <button type="button" class="ghost" :disabled="loading" @click="refresh">
          <NavIcon name="refresh" :size="14" />
          刷新
        </button>
      </template>
    </PageHeader>

    <ErrorBanner tone="dark" title="上一次加载失败" :message="lastError" />

    <PanelCard tone="dark" padding="none" glow>
      <div class="bar">
        <div class="filters">
          <n-select
            v-model:value="filterStatus"
            class="filter-status"
            :options="statusOptions"
            placeholder="全部状态"
            clearable
            @update:value="applyFilters"
          />
          <n-input
            v-model:value="filterAircraftModel"
            class="filter-text"
            placeholder="按机型模糊匹配"
            @keyup.enter="applyFilters"
          />
          <n-input
            v-model:value="filterOrderNo"
            class="filter-text"
            placeholder="按订单号搜索"
            @keyup.enter="applyFilters"
          />
          <n-button size="small" @click="applyFilters">筛选</n-button>
          <n-button size="small" quaternary @click="clearFilters">清空</n-button>
        </div>
        <span class="page-text">共 {{ total }} 条 · 第 {{ page }} / {{ pageCount }} 页</span>
      </div>
      <DataTable
        tone="dark"
        :columns="columns"
        :data="rows"
        :row-key="(row: TakeoffApplication) => row.id"
        :loading="loading"
        :pagination="{ page, pageSize: PAGE_SIZE, itemCount: total, onPage: goPage }"
        empty-text="还没有起飞申请"
      />
    </PanelCard>

    <n-drawer v-model:show="drawerOpen" :width="640" placement="right">
      <n-drawer-content v-if="target" :native-scrollbar="false">
        <template #header>
          <div class="drawer-head">
            <span class="drawer-title">{{ target.orderNo }}</span>
            <StatusTag
              :status="target.status"
              :label="takeoffApplicationStatusLabel(target.status)"
              :group="takeoffApplicationStatusGroup(target.status)"
              tone="dark"
            />
          </div>
        </template>

        <div class="drawer-body">
          <PanelCard tone="dark" title="申报内容">
            <div v-if="detailLoading" class="hint-text">正在读取这条申请的完整内容…</div>
            <template v-else-if="detailError !== null">
              <ErrorBanner tone="dark" :message="detailError" title="读取申请详情失败" />
              <n-space align="center" class="retry">
                <n-button size="tiny" @click="target && loadDetail(target.id)">重试</n-button>
                <span class="hint-text">读不出来时不显示任何替代值 ——「没读到」与「这条申请没有内容」不是一回事</span>
              </n-space>
            </template>
            <FieldGrid v-else :items="declaredFields" tone="dark" />
          </PanelCard>

          <PanelCard v-if="target.status !== 'pending'" tone="dark" title="审核结论">
            <FieldGrid :items="reviewFields" tone="dark" />
          </PanelCard>

          <PanelCard v-if="target.status === 'pending'" tone="dark" title="审核">
            <div class="field-label">审核意见(驳回必须填,通过选填;最多 {{ REVIEW_NOTE_MAX }} 字)</div>
            <n-input
              v-model:value="note"
              type="textarea"
              :rows="3"
              :maxlength="REVIEW_NOTE_MAX"
              show-count
              placeholder="驳回时写清该改什么;通过时可留空"
            />
            <n-space class="review-actions">
              <n-button size="small" type="primary" :loading="submitting" @click="submitReview(true)">通过</n-button>
              <n-button size="small" :loading="submitting" @click="submitReview(false)">驳回</n-button>
            </n-space>
          </PanelCard>
        </div>
      </n-drawer-content>
    </n-drawer>
  </div>
</template>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 刷新:与其它深色页同一枚幽灵键(无底、1px 描边 border-strong、内边距 [8,14]) */
.ghost {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid var(--sd-border-strong);
  border-radius: var(--sd-radius-sm);
  background: transparent;
  color: var(--sd-text2);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.ghost:hover:not(:disabled) {
  color: var(--sd-text1);
  border-color: var(--sd-brand);
}

.ghost:disabled {
  opacity: 0.6;
  cursor: default;
}

/* 计数行:筛选在左、页码在右 */
.bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 16px 20px;
}

.filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-status {
  width: 150px;
}

.filter-text {
  width: 190px;
}

.page-text {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--sd-text3);
}

.drawer-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.drawer-title {
  font-family: var(--sd-font-mono);
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  color: var(--sd-text1);
}

.drawer-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.hint-text {
  font-size: 12.5px;
  line-height: 1.7;
  color: var(--sd-text3);
}

.retry {
  margin-top: 12px;
}

.field-label {
  font-size: 12px;
  color: var(--sd-text3);
  margin-bottom: 6px;
}

.review-actions {
  margin-top: 12px;
}
</style>
